(ns shared.core.util
  (:require [cljs.core.async :refer [<! timeout]]
            [cljs.reader :refer [read-string]]
            [clojure.string :as s]
            [clojure.set :refer [subset? union]]
            [shared.core.utils.currency :as currency]
            [camel-snake-kebab.core :as csk]
            [camel-snake-kebab.extras :refer [transform-keys]]
            [hickory.core :as hickory]
            [garden.color :as gcolor]
            [goog.string :as gstring]
            [re-frame.core :refer [console]]
            [shared.core.regexes :as regexes]
            [cljs-time.core :as cljs-time]
            [cljs-time.format :as format]
            [malli.core :as m])
  (:require-macros [cljs.core.async.macros :refer [go]])
  (:import [goog.async Debouncer]))

(defn ->xml-link
  "Assumes a browser environment, creates a browser link from an xml-string. Takes an optional filename as a second argument."
  ([xml-string]
   (->xml-link xml-string "converted-string.xml"))
  ([xml-string filename]
   (-> js/URL (.createObjectURL (new js/File [xml-string] filename {:type "text/xml"})))))

(defn capitalize-words [s]
  (some->> (s/split s " ")
           (map s/capitalize)
           (s/join " ")))

(defn ->capitalize-words [k]
  (try
    (some->> (s/split (csk/->kebab-case (name k)) "-")
             (s/join " ")
             (capitalize-words))
    (catch :default _e
      k)))

(defn not-blank-string? [in-s]
  (not (s/blank? (str in-s))))

(defn key-event? [key e]
  (= key (.-which e)))

(defn enter-key-event? [e]
  (key-event? 13 e))

(defn escape-key-event? [e]
  (key-event? 27 e))

(defn up-key-event? [e]
  (key-event? 38 e))

(defn down-key-event? [e]
  (key-event? 40 e))

(defn momentum-scroll-x-style [s]
  (merge {:overflow-x :scroll
          :-webkit-overflow-scrolling :touch}
         s))

(defn momentum-scroll-y-style [s]
  (merge {:overflow-y :scroll
          :-webkit-overflow-scrolling :touch}
         s))

(defn hex->rgba [color opacity]
  (try
    (let [{:keys [red green blue]} (gcolor/hex->rgb color)]
      (str "rgba("
           red
           ","
           green
           ","
           blue
           ","
           opacity
           ")"))
    (catch :default _
      color)))

(defn stop-color
  "The color of the band `value` falls in. `stops` are ascending inclusive upper
   bounds and `colors` has one color per stop plus a final one for anything above
   the last stop — stops [30 60] with colors [red orange green] is <= 30 red,
   <= 60 orange, otherwise green."
  [value stops colors]
  (when (number? value)
    (let [band (->> stops
                    (take-while #(> value %))
                    (count))]
      (nth colors band nil))))

(defn stop-labels
  "One legend label per `stop-color` band: `labels` where given, otherwise the band's
   range with each stop formatted by `fmt` — stops [30 60] is \"≤ 30\", \"30 – 60\",
   \"> 60\"."
  [stops labels fmt]
  (map-indexed (fn [i label]
                 (or (not-empty label)
                     (cond
                       (empty? stops) ""
                       (zero? i) (str "≤ " (fmt (first stops)))
                       (= i (count stops)) (str "> " (fmt (last stops)))
                       :else (str (fmt (nth stops (dec i))) " – " (fmt (nth stops i))))))
               (take (inc (count stops))
                     (concat labels (repeat nil)))))

(defn lighten-hex
  "Lighten a hex color by `amount` (0-1)."
  [hex-color amount]
  (let [{:keys [red green blue]} (gcolor/hex->rgb hex-color)
        lighten (fn [component]
                  (Math/round (+ component (* (- 255 component) amount))))]
    (str "#"
         (.padStart (.toString (lighten red) 16) 2 "0")
         (.padStart (.toString (lighten green) 16) 2 "0")
         (.padStart (.toString (lighten blue) 16) 2 "0"))))

(defn darken-hex
  "Darken a hex color by `amount` (0-1)."
  [hex-color amount]
  (let [{:keys [red green blue]} (gcolor/hex->rgb hex-color)
        darken (fn [component]
                 (Math/round (* component (- 1 amount))))]
    (str "#"
         (.padStart (.toString (darken red) 16) 2 "0")
         (.padStart (.toString (darken green) 16) 2 "0")
         (.padStart (.toString (darken blue) 16) 2 "0"))))

(defn mac? []
  (re-find #"(Mac|iPhone|iPod|iPad)" js/navigator.platform))

(defn escape-quotes [i]
  (some-> i
          (clojure.string/replace #"\\\"" "\\\\\"")
          (clojure.string/replace #"\"" "\\\"")))

(defn replace-all-ignore-case [text match replacement]
  (.replace text (js/RegExp. (gstring/regExpEscape match) "gi") (constantly replacement)))

(defn replace-all-ignore-case-and-punctuation
  "Like `replace-all-ignore-case`, but `match` only has to agree on its letters and digits,
   so \"Fuzzys Taco Shop\" also replaces \"Fuzzy's Taco-Shop\". Never matches mid-word.
   `capitalized?` only matches where the first letter is uppercase in `text`."
  [text match replacement & [{:keys [capitalized?]}]]
  (if-let [chars (some->> (s/replace match #"(?i)[^a-z0-9]" "") (not-empty) (seq))]
    (.replace text
              (js/RegExp. (str "(?<![A-Za-z0-9])"
                               (->> chars
                                    (map-indexed (fn [idx c]
                                                   (if (and capitalized? (zero? idx))
                                                     (s/upper-case c)
                                                     (str "[" (s/lower-case c) (s/upper-case c) "]"))))
                                    (s/join "[^A-Za-z0-9]*"))
                               "(?![A-Za-z0-9])")
                          "g")
              (constantly replacement))
    text))

(defn wait [time func]
  (go
    (<! (timeout time))
    (func)))

(defn loading? [thing]
  (= thing :loading))

(defn nil-or-loading? [this]
  (or (nil? this) (loading? this)))

(defn not-loading? [thing]
  (not= thing :loading))

(defn when-not-loading [thing]
  (when (not-loading? thing) thing))

(defn when-not-empty [thing]
  (when (not-empty thing) thing))

(defn ->map-without-nil-vals [m]
  (some->> m
           (remove #(nil? (second %)))
           (into {})))

(defn ->camelCaseKeywords [m]
  (transform-keys csk/->camelCaseKeyword m))

(defn ->camelCaseString [m]
  (transform-keys csk/->camelCaseString m))

(def ->camelCaseKeyword csk/->camelCaseKeyword)

(defn ->snake_case_string [m]
  (transform-keys csk/->snake_case_string m))

(def ->SCREAMING_SNAKE_CASE_STRING csk/->SCREAMING_SNAKE_CASE_STRING)

(def ->kebab-case-keyword csk/->kebab-case-keyword)

(defn ->kebab-case-keywords [m]
  (transform-keys csk/->kebab-case-keyword m))

(defn ->kebab-case-keyword-top-level [m]
  (if (map? m)
    (->> m
         (mapv (fn [[k v]]
                 [(->kebab-case-keyword k)
                  v]))
         (into {}))
    m))

(defn ->kebab-case-string [m]
  ;; Adding map check on this one because it's improperly named but I don't want to break places using it as is
  (if (map? m)
    (transform-keys csk/->kebab-case-string m)
    (csk/->kebab-case-string m)))

(defn char-code [char]
  (.charCodeAt char 0))

(def readable-number currency/readable-number)

(defn ->kebab-case-input-formatter [i]
  (str (csk/->kebab-case (s/lower-case i))
       (when (or (s/ends-with? i " ")
                 (s/ends-with? i "-"))
         "-")))

(defn str->hex [string]
  (let [hex (-> (reduce (fn [prev char]
                          (+ (char-code char) (- (bit-shift-left prev 5) prev)))
                        0
                        string)
                (bit-and 0x00FFFFFF)
                (.toString 16))]
    (str "#"
         (apply str (repeat (- 6 (count hex)) "0"))
         hex)))

(defn remove-new-lines [text & {:keys [replace-with]
                                :or {replace-with " "}}]
  (s/replace text "\n" replace-with))

(defn parse-int [n]
  (try
    (cond
      (int? n) n
      (s/blank? n) nil
      (string? n) (-> n
                      (s/replace "," "")
                      (js/parseInt))
      :else (js/parseInt n))
    (catch js/Error _
      nil)))

(defn parse-float [n]
  (try
    (cond
      (float? n) n
      (s/blank? n) nil
      :else (js/parseFloat n))
    (catch js/Error _
      nil)))

(defn query-int [args k]
  (some-> (get args k) parse-int))

(defn to-precision
  "Converts num to rounded precision string"
  [num & {:keys [precision] :or {precision 2}}]
  (gstring/format (str "%." precision "f") num))

(defn to-precision-num
  "Converts num to rounded precision string"
  [num & {:keys [precision] :or {precision 2}}]
  (if (= precision 0)
    (Math/round num)
    (/ (Math/round (* num (* precision 10))) (* precision 10))))

(defn abbreviated-large-number [num & {:keys [precision]}]
  (let [divide-by-and-round-with-suffix (fn [divide-by suffix]
                                          (str (if precision
                                                 (to-precision-num (/ num divide-by) :precision precision)
                                                 (Math/round (/ num divide-by)))
                                               suffix))]
    (cond
      (nil? num) nil
      (< num 1000) (readable-number num)
      (< num 1000000) (divide-by-and-round-with-suffix 1000 "K")
      (< num 1000000000) (divide-by-and-round-with-suffix 1000000 "M")
      (< num 1000000000000) (divide-by-and-round-with-suffix 1000000000 "B")
      :else (divide-by-and-round-with-suffix 1000000000000 "T"))))

(defn map->json-string [m]
  (-> (transform-keys #(try
                         (csk/->camelCase %)
                         (catch :default _
                           %))
                      m)
      (clj->js)
      (js/JSON.stringify nil "\t")))

(defn json->clj [json-string]
  (try
    (some-> json-string
            (js/JSON.parse)
            (js->clj))
    (catch :default _
      nil)))

(defn json->clj-keywordized
  [json-string]
  (try
    (when-not (s/blank? json-string)
      (js->clj (js/JSON.parse json-string) :keywordize-keys true))
    (catch :default _
      nil)))

(defn clj->json-string
  [m]
  (try
    (js/JSON.stringify (clj->js m) nil 2)
    (catch :default _
      nil)))

(defn normalize-to-json-string
  [value]
  (cond
    (or (nil? value) (and (string? value) (s/blank? value))) "{}"
    (string? value) value
    (map? value) (or (clj->json-string value) "{}")
    :else "{}"))

(defn valid-json? [json-string]
  (try
    (when (some-> json-string (js/JSON.parse))
      true)
    (catch :default _
      false)))

(defn collect-keys [m]
  (cond
    (map? m) (into (set (keys m))
                   (mapcat collect-keys (vals m)))
    (coll? m) (apply union (map collect-keys m))
    :else #{}))

(defn valid-json-keys? [json-string allowed-keys]
  (try
    (let [maps (some-> json-string
                       (js/JSON.parse)
                       (js->clj :keywordize-keys true))
          curr-keys (collect-keys maps)]
      (if (seq curr-keys)
        (subset? curr-keys allowed-keys)
        false))
    (catch :default _
      false)))

(defn validate-value-with-schema
  [value schema]
  (m/validate schema value))

(defn valid-json-with-schema?
  [json-string schema]
  (try
    (let [parsed (js/JSON.parse json-string)
          clj-value (js->clj parsed :keywordize-keys true)]
      (if (validate-value-with-schema clj-value schema)
        nil
        "JSON does not match the required schema"))
    (catch :default e
      "Invalid JSON format")))

(defn humanize-malli-errors
  [errors]
  (when (seq errors)
    (for [error errors]
      (let [{:keys [path in type message value]} error
            field-path (if (seq in)
                         (s/join " → " (map #(if (number? %)
                                               (str "[" % "]")
                                               (name %)) in))
                         "root")
            path-str (fn [p]
                       (cond
                         (keyword? p) (name p)
                         (string? p) p
                         (number? p) (str p)
                         :else (str p)))

            human-message (case type
                            :malli.core/missing-key
                            (str "Missing required field: " (path-str (first path)))

                            :malli.core/invalid-type
                            (str "Invalid type at " field-path ": expected " message)

                            :malli.core/input-remaining
                            (str "Unexpected data at " field-path)

                            :malli.core/end-of-input
                            (str "Incomplete data at " field-path)

                            :malli.core/invalid-dispatch-value
                            (str "Invalid value at " field-path)

                            :malli.core/limits
                            (str "Value out of bounds at " field-path)

                            ;; Default - try to extract useful info
                            (str "Validation error at " field-path " " value
                                 (when message (str ": " message))))]
        {:field field-path
         :message human-message
         :value value}))))

(defn explain-json-schema-error
  [json-string schema]
  (try
    (let [parsed (js/JSON.parse json-string)
          clj-value (js->clj parsed :keywordize-keys true)]
      (when-not (m/validate schema clj-value)
        (let [explanation (m/explain schema clj-value)
              raw-errors (:errors explanation)
              human-errors (humanize-malli-errors raw-errors)]
          {:message "JSON does not match the required schema"
           :human-errors human-errors
           :errors (pr-str raw-errors)
           :value (:value explanation)})))
    (catch :default e
      {:message "Invalid JSON format"
       :errors (str e)})))

(defn format-json
  [json-string]
  (try
    (let [parsed (js/JSON.parse json-string)]
      (js/JSON.stringify parsed nil 2))
    (catch :default e
      json-string)))

(defn format-json-structure
  "`json-string` indented when it holds a JSON object or array, else nil."
  [json-string]
  (try
    (let [parsed (some-> json-string (js/JSON.parse))]
      (when (or (array? parsed) (object? parsed))
        (js/JSON.stringify parsed nil 2)))
    (catch :default _
      nil)))

(defn minify-json
  [json-string]
  (try
    (js/JSON.stringify (js/JSON.parse json-string))
    (catch :default e
      json-string)))

(defn json-string->map [json-string]
  (try
    (some-> json-string
            (json->clj)
            (->kebab-case-keywords))
    (catch :default _
      nil)))

(defn json-string->map-basic [json-string]
  (try
    (some-> json-string (json->clj))
    (catch :default _
      nil)))

(defn map->json-string-min [m]
  (-> (transform-keys csk/->camelCase m)
      (clj->js)
      (js/JSON.stringify)))

(defn stop-event [e]
  (.stopPropagation e)
  (.preventDefault e))

(defn without-nils-map [m]
  (apply merge (for [[k v] m :when (not (nil? v))] {k v})))

(defn vec-remove [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(defn vec-add [coll pos el]
  (concat (subvec coll 0 pos) [el] (subvec coll pos)))

(defn vec-move [coll pos1 pos2]
  (let [el (nth coll pos1)]
    (if (= pos1 pos2)
      coll
      (into [] (vec-add (vec-remove coll pos1) pos2 el)))))

(defn vec-swap [v pos1 pos2]
  (assoc v pos2 (v pos1) pos1 (v pos2)))

(defn debounce
  [f interval]
  (let [debouncer (Debouncer. f interval)]
    (fn [& args] (.apply (.-fire debouncer) debouncer (to-array args)))))

(defn group-by-distinct
  "`group-by` but with the assumption that each key is unique"
  [func coll]
  (->> (group-by func coll)
       (map (fn [[k v]] [k (first v)]))
       (into {})))

(defn distinct-by
  "Runs a distinct on a collection using the specified function"
  [func coll]
  (->> (group-by-distinct func coll)
       (mapv (fn [[_ v]] v))))

(defn sort-by-reverse [keyfn coll]
  (some->> coll
           (sort-by keyfn)
           (reverse)))

(defn insert-at-position [items item position]
  (let [v-items (vec items)]
    (concat (subvec v-items 0 position) [item] (subvec v-items position))))

(defn smooth-scroll-to-view [el]
  (.scrollIntoView el (clj->js {:behavior "smooth"})))

(defn instant-scroll-to-view [el]
  (.scrollIntoView el (clj->js {:behavior "instant"})))

(defn ns->keyword-path [n]
  (if (and (keyword? n) (namespace n))
    (let [kp (->> (s/split (namespace n) #"\.")
                  (mapv csk/->kebab-case-keyword))]
      (if (= (last kp) :data) (pop kp) kp))
    (throw (js/Error. (str "failed to initialize root-path. " n " is not a namespace keyword")))))

(defn js-obj->kebab-case-keywords [js-obj]
  (transform-keys
    csk/->kebab-case-keyword
    (js->clj js-obj)))

(defn pluralize [w count & {:keys [plural] :or {plural "s"}}]
  (str w (when (not= count 1) plural)))

(defn html->hiccup [html]
  (-> html
      (hickory/parse)
      (hickory/as-hiccup)
      (first)))

(defn hiccup-is-tag? [hiccup tag]
  (= (first hiccup) tag))

(defn find-first-hiccup-tag [hiccup tag]
  (cond
    (not (vector? hiccup)) nil
    (hiccup-is-tag? hiccup tag) hiccup
    :else (->> hiccup
               (map #(find-first-hiccup-tag % tag))
               (filter identity)
               (first))))

(defn find-all-hiccup-tags
  ([hiccup tag]
   (find-all-hiccup-tags hiccup tag []))
  ([hiccup tag found]
   (cond
     (not (vector? hiccup)) nil
     (= (first hiccup) tag) [hiccup]
     :else (->> hiccup
                (map #(find-all-hiccup-tags % tag found))
                (filter identity)
                (apply concat found)))))

(defn find-all-of-type
  ([items type?]
   (find-all-of-type items type? []))
  ([items type-check? found]
   (cond
     (type-check? items) [items]
     (vector? items) (->> items
                          (map #(find-all-of-type % type-check? found))
                          (filter identity)
                          (apply concat found))
     :else nil)))

(defn pct-red-to-green-color [pct]
  (let [low-range? (and (>= pct 0) (<= pct 0.5))
        r (if low-range? 255 (+ (* -510 pct) 510))
        g (if low-range? (* 510 pct) 255)
        shade-mult 0.75]
    (str "rgb(" (* r shade-mult) "," (* g shade-mult) ",0)")))

(defn just-da-digits [num]
  (some->> num
           (str)
           (re-seq #"\d+")
           (s/join "")))

(defn first-word [words]
  (when (string? words)
    (-> words (s/split #" ") (first))))

(defn first-word-contains-num? [words]
  (when (string? words)
    (when-let [first-word (first-word words)]
      (not (nil? (just-da-digits first-word))))))

(defn readable-phone-number [phone-number]
  (let [phone-number-digits (just-da-digits phone-number)]
    (condp = (count phone-number-digits)
      11 (str (subs phone-number-digits 0 1)
              " ("
              (subs phone-number-digits 1 4)
              ") "
              (subs phone-number-digits 4 7)
              "-"
              (subs phone-number-digits 7 11))
      10 (str "("
              (subs phone-number-digits 0 3)
              ") "
              (subs phone-number-digits 3 6)
              "-"
              (subs phone-number-digits 6 10))
      phone-number)))

(defn deg->rad [deg]
  (* js/Math.PI (/ deg 180)))

(defn rad->deg [rad]
  (* rad (/ 180 js/Math.PI)))

(defn distance-between-two-coordinates
  "Returns the distance between two latitude / longitude pairs in miles"
  [[lat1 long1] [lat2 long2]]
  (if (and (= lat1 lat2) (= long1 long2))
    0
    (let [rad-lat1 (deg->rad lat1)
          rad-lat2 (deg->rad lat2)
          theta (- long1 long2)
          rad-theta (deg->rad theta)
          v (+ (* (js/Math.sin rad-lat1) (js/Math.sin rad-lat2))
               (* (js/Math.cos rad-lat1) (js/Math.cos rad-lat2) (js/Math.cos rad-theta)))]
      (-> (if (> v 1) 1 v)
          (js/Math.acos)
          (rad->deg)
          (* 60 1.1515)))))

;; Aliasing a more flexible function in the currency namespace
(def pennies->readable-price currency/currency-int->readable-price)

(defn pennies->dollars
  [pennies]
  (/ (or pennies 0) 100.0))

(defn flatten-tree [{:keys [children sort-children-fn] :as i}]
  (let [children (if sort-children-fn (sort-children-fn children) children)]
    (concat [i] (flatten (map #(flatten-tree (assoc % :sort-children-fn sort-children-fn)) children)))))

(defn ->formatted-address [{:keys [city state country street-address-1 street-address-2 zip]}]
  (->> [(str street-address-1 " " street-address-2) city state country zip]
       (map #(s/trim (str %)))
       (filter #(not (s/blank? %)))
       (s/join ", ")))

(defn string->boolean [in-s]
  (if (= (s/lower-case in-s) "true")
    true
    false))

(defn lower-case-includes [string sub-string]
  (s/includes? (s/lower-case string) (s/lower-case sub-string)))

(defn normalize-for-search
  "Normalizes a string for searching by removing special characters and converting to lowercase.
   Useful for fuzzy text matching where special characters should be ignored.
   Example: 'DTMF:*81111#*2' becomes 'dtmf81111*2'"
  [s]
  (when s
    (-> s
        str
        s/lower-case
        (s/replace #"[!@#$%^&*()_+=\[\]{};:'\"\\|,.<>/?~`-]" ""))))

(defn browser-dark-mode? []
  (try
    (when (exists? js/window.matchMedia)
      (some-> (js/window.matchMedia "(prefers-color-scheme: dark)") (.-matches)))
    (catch :default _
      false)))

(defn conditionally-enabled-devtools-dark-mode []
  (when (and goog.DEBUG (browser-dark-mode?))
    (let [{:keys [cljs-land-style]} (devtools.core/get-prefs)]
      (devtools.core/set-pref! :cljs-land-style (str "filter:invert(1);" cljs-land-style)))))

(defn tap [& inputs]
  (apply console (concat [:log "🚰"] inputs))
  (last inputs))

(defn print+return [& d]
  (apply println d)
  (last d))

(defn pprint+return [& d]
  (apply cljs.pprint/pprint d)
  (last d))

(defn base-64-decode [to-decode]
  (try
    (js/atob to-decode)
    (catch :default _
      nil)))

(defn base-64-encode [to-encode]
  (try
    (js/btoa to-encode)
    (catch :default _
      nil)))

(defn url-friendly-base-64-encode [data]
  (-> (base-64-encode data)
      (s/replace #"\+" ".")
      (s/replace #"\\" "_")
      (s/replace #"\=" "-")))

(defn url-friendly-base-64-decode [url-friendly-encoded-string]
  (-> (s/replace url-friendly-encoded-string #"\." "+")
      (s/replace #"_" "/")
      (s/replace #"-" "=")
      (base-64-decode)))

(defn items-by-unique-key [k items]
  (->> items
       (group-by k)
       (map (fn [[k [v]]] [k v]))
       (into {})))

(defn some-trim [in-string]
  (some-> in-string (s/trim)))

(defn safe-trim [in-string]
  (s/trim (str in-string)))

(defn try-nth [coll n]
  (try
    (nth coll n)
    (catch :default _
      nil)))

(defn bounding-rect->map
  [bounding-rect]
  (when bounding-rect
    {:x (.-x bounding-rect)
     :y (.-y bounding-rect)
     :width (.-width bounding-rect)
     :height (.-height bounding-rect)
     :top (.-top bounding-rect)
     :right (.-right bounding-rect)
     :bottom (.-bottom bounding-rect)
     :left (.-left bounding-rect)}))

(defn bounding-rect-map-by-container-id
  [container-id]
  (some-> (.getElementById js/document container-id)
          (.getBoundingClientRect)
          (bounding-rect->map)))

(defn pct->pct-string [pct & {:keys [precision]}]
  (-> (* pct 100)
      (to-precision :precision (or precision 0))
      (str "%")))

(defn insta-payment?
  "Determines if card is of type `InstaPayment`.
  Example Card Number: 6378888888888888"
  [card-num]
  (re-matches #"^63[7-9][0-9]{13}$" (str card-num)))

(defn string-uuid? [potential-string-uuid]
  (re-matches #"^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$" potential-string-uuid))

(defn strip-ssml-speak-tags [ssml]
  (cond-> ssml
          (s/starts-with? ssml "<speak>") (subs 7)
          (s/ends-with? ssml "</speak>") (#(subs % 0 (- (count %) 8)))))

(defn try-read-string [in-s]
  (try
    (read-string in-s)
    (catch :default _
      nil)))

(defn valid-reframe-event-string? [in-s]
  (and (string? in-s)
       (try
         (let [val (-> in-s (read-string))]
           (and (vector? val) (-> val (first) (keyword?))))
         (catch :default _
           false))))

(defn window-url-params []
  (let [window-location-search (.. js/window -location -search)]
    (when (not (s/blank? window-location-search))
      (when-let [kvs (some-> (.toString (new js/URLSearchParams window-location-search))
                             (s/split #"&"))]
        (->> kvs
             (map #(s/split % #"="))
             (map (fn [[k v]]
                    [k (or v "")]))
             (into {})
             (->kebab-case-keywords))))))

(defn string-is-uuid? [in-s]
  (and (string? in-s) (re-matches regexes/uuid?? in-s)))

(defn valid-optional-uuid?
  "Returns true if the value is blank or a valid UUID string.
   Useful for validating optional UUID form fields."
  [s]
  (or (s/blank? s)
      (string-is-uuid? s)))

(defn sort-map-by-keys [m keys-ordered]
  (let [keys->idx (zipmap keys-ordered (range))
        order-fn (fn [x y] (< (keys->idx x) (keys->idx y)))]
    (into (sorted-map-by order-fn) m)))

(defn scroll-to-bottom-by-selector [selector]
  (when-let [el (.querySelector js/document selector)]
    (set! (.-scrollTop el) (.-scrollHeight el))))

(defn flatten-items-list [{:keys [sort-fn children-key] :or {children-key :children} :as args} items]
  (->> (if sort-fn (sort-fn items) items)
       (map (fn [i] (concat [i] (flatten-items-list args (get i children-key)))))
       (flatten)
       (filter identity)))

(defn download-blob [{:keys [blob url download-name]}]
  (let [object-url (when blob
                     (js/URL.createObjectURL blob))
        href (or object-url url)
        a (js/document.createElement "a")]
    (set! (.-target a) "_blank")
    (set! (.-href a) href)
    (when download-name
      (set! (.-download a) download-name))
    (-> js/document .-body (.appendChild a))
    (.click a)
    (-> js/document .-body (.removeChild a))))

(defn cdn-ui-public-url [uri]
  (str "https://ui-cdn.novolabs.com"
       (when-not (s/starts-with? uri "/") "/")
       uri))

(defn cdn-ui-public-css-url [uri]
  (str "url('" (cdn-ui-public-url uri) "')"))

(defn scroll-to-dom-el-by-id [dom-id args]
  (some-> dom-id
          (js/document.getElementById)
          (.scrollIntoView (clj->js (merge {:block "start" :inline "start" :behavior "smooth"}
                                           args)))))

(defn detect-scroll-end [el callback]
  (try
    (let [detected-no-movement?* (atom false)               ;; This gives a little buffer just to double check we are done here
          window-scroll-y? (and (= el js/document.body) (pos? (.-scrollY js/window)))
          last-position* (atom (if window-scroll-y?
                                 (.-scrollY js/window)
                                 (.-scrollTop el)))]
      (letfn [(check-scroll []
                (let [current-position (if window-scroll-y?
                                         (.-scrollY js/window)
                                         (.-scrollTop el))
                      no-movement? (= current-position @last-position*)]
                  (if (and no-movement? @detected-no-movement?*)
                    (callback)
                    (do
                      (when no-movement?
                        (reset! detected-no-movement?* true))
                      (reset! last-position* current-position)
                      (js/requestAnimationFrame check-scroll)))))]
        (js/requestAnimationFrame check-scroll)))
    (catch :default _
      (callback))))

(defn wait-for-scroll-end [el callback]
  (wait 100 #(detect-scroll-end el callback)))

(defn ipv6? [ip-str]
  (try
    ;; Newer Closure bundles used by shadow-cljs no longer expose goog.net.IpAddress.
    ;; Let the platform URL parser validate bracketed IPv6 hosts instead.
    (= (.-hostname (js/URL. (str "http://[" ip-str "]/")))
       (str "[" ip-str "]"))
    (catch :default _
      false)))

(defn ios? []
  (try
    (let [platforms ["iPad Simulator" "iPhone Simulator" "iPod Simulator"
                     "iPad" "iPhone" "iPod"]
          user-agent (.-userAgent js/navigator)
          platform (.-platform js/navigator)]
      (or (some #(= platform %) platforms)
          (and (.includes user-agent "Mac") (.-ontouchend js/document))))
    (catch :default _
      false)))

(defn mobile? []
  (try
    (let [regex (js/RegExp. "Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini" "i")]
      (.test regex (.-userAgent js/navigator)))
    (catch :default _
      false)))

(defn safari? []
  (try
    (and (.-vendor js/navigator)
         (> (.indexOf (.-vendor js/navigator) "Apple") -1)
         (.-userAgent js/navigator)
         (= -1 (.indexOf (.-userAgent js/navigator) "CriOS"))
         (= -1 (.indexOf (.-userAgent js/navigator) "FxiOS")))
    (catch :default _
      false)))

(defn remove-nil-from-map [m]
  (->> m
       (filter (fn [[_ v]] (not (nil? v))))
       (into {})))

(def default-date-format "yyyy-MM-dd")

(defn format-date
  "Formats a date using the specified format string.
   If no format is provided, uses the default yyyy-MM-dd format."
  ([date]
   (format-date date default-date-format))
  ([date format-str]
   (format/unparse (format/formatter format-str) date)))

(defn weeks-ago
  "Returns a date that is n weeks before the current date"
  [n]
  (cljs-time/minus (cljs-time/now) (cljs-time/weeks n)))

(defn days-ago
  "Returns a date that is n days before the current date"
  [n]
  (cljs-time/minus (cljs-time/now) (cljs-time/days n)))

(defn create-default-date-filter
  "Creates a default date filter with start-date-time set to n weeks ago.
   Defaults to 1 week ago if no parameter is provided."
  ([]
   (create-default-date-filter 1))
  ([weeks-back]
   {:start-date-time (-> (weeks-ago weeks-back)
                         (format-date))}))

(defn path-exists? [m ks]
  (loop [m m [k & ks] ks]
    (and (map? m)
         (contains? m k)
         (or (nil? ks)
             (recur (get m k) ks)))))

(defn index-of [i v]
  (try
    (.indexOf (to-array v) i)
    (catch :default _
      nil)))

(defn readable-hour-num
  "Formats an hour number (0-23) to '7:00 AM' format"
  [hour-num]
  (when (some? hour-num)
    (try
      (let [hour-num (parse-int hour-num)
            hour-12 (cond
                      (zero? hour-num) 12
                      (> hour-num 12) (- hour-num 12)
                      :else hour-num)
            am-pm (if (< hour-num 12) "AM" "PM")]
        (str hour-12 ":00 " am-pm))
      (catch :default _
        (str (parse-int hour-num) ":00")))))

(defn string->hash [in-s]
  (str (reduce
         (fn [hash ch]
           (let [code (.charCodeAt ch 0)]
             (bit-or
               0
               (+ (- (bit-shift-left hash 5) hash)
                  code))))
         0
         in-s)))

(defn time-of-day-with-leading-zero [in-tod]
  (if (some-> in-tod
              (s/split #":")
              (first)
              (count)
              (= 1))
    (str "0" in-tod)
    in-tod))

(defn stringify-map-values
  "Converts map values to strings for filtering/search compatibility.
   Numbers, keywords, booleans become strings. Collections (non-maps) become comma-separated strings.
   Maps and nil are left as-is."
  [m]
  (reduce-kv (fn [acc k v]
               (assoc acc k (cond
                              (or (number? v) (keyword? v) (boolean? v)) (str v)
                              (and (coll? v) (not (map? v))) (s/join ", " (map str v))
                              :else v)))
             {}
             m))

(defn resize-observer [element-selector on-change]
  (let [observer (new js/ResizeObserver on-change)]
    (some->> element-selector (js/document.querySelector) (.observe observer))))

(defn size-ref
  "A React `:ref` callback keeping `size*` in sync with its own element's content-box
   size (`{:width :height}`), so a component can lay itself out from the space it
   actually has. Disconnects when the element detaches. Create it once per component
   instance (in a form-2 outer let) — React re-attaches a ref whose identity changed."
  [size*]
  (let [observer* (atom nil)]
    (fn [el]
      (some-> @observer* (.disconnect))
      (reset! observer* nil)
      (when el
        (reset! size* {:width (.-clientWidth el)
                       :height (.-clientHeight el)})
        (when (exists? js/ResizeObserver)
          (let [observer (new js/ResizeObserver
                              (fn [entries]
                                (when-let [rect (some-> entries (array-seq) (first) (.-contentRect))]
                                  (reset! size* {:width (.-width rect)
                                                 :height (.-height rect)}))))]
            (.observe observer el)
            (reset! observer* observer)))))))

(defn users-tz []
  (some-> js/Intl
          (.DateTimeFormat)
          (.resolvedOptions)
          .-timeZone))

(defn deep-merge [a b]
  (if (and (map? a) (map? b))
    (merge-with deep-merge a b)
    b))

(defn update-when-not-nil [d k update-fn]
  (update d k #(when (not (nil? %)) (update-fn %))))

(defn random-uuid-string []
  (str (random-uuid)))

(defn by-key
  "Indexes a collection of items into a map based on a specified key `k`.
  Safely ignores the collection if it is currently in a loading state."
  [items k]
  (->> (when-not-loading items)
       (map (juxt k identity))
       (into {})))

(defn soundhound-email? [email-address]
  (boolean (some-> email-address (s/trim) (s/lower-case) (s/ends-with? "@soundhound.com"))))

(defn parse-int-ids
  "Removes nil elements from a collection and parses the rest with `parse-int`,
   returning a vector of integers."
  [v]
  (->> v
       (remove nil?)
       (map parse-int)
       vec))

(defn join-labels
  "Returns a comma-separated, alphabetically sorted string of labels from a
   collection of items matching a given set of item IDs.
   Returns a string, or an empty string if no valid labels are found."
  ([items item-ids]
   (join-labels items item-ids :id :label))
  ([items item-ids label-k]
   (join-labels items item-ids :id label-k))
  ([items item-ids id-k label-k]
   (let [items-by-id (by-key items id-k)]
     (->> (parse-int-ids item-ids)
          (map #(-> items-by-id (get %) label-k))
          (remove s/blank?)
          (sort)
          (s/join ", ")))))

(defn find-scroll-container [el]
  (loop [node (.-parentElement el)]
    (when node
          (let [overflow-y (-> node js/getComputedStyle .-overflowY)]
            (if (contains? #{"auto" "scroll" "overlay"} overflow-y)
              node
              (recur (.-parentElement node)))))))

(defn scrolled-near-bottom?
  "Whether `el` is scrolled to within `buffer-px` of the end of its content — the cue an
   infinite-scrolling list uses to ask for its next page."
  [el buffer-px]
  (boolean
    (when el
      (<= (- (.-scrollHeight el) (.-scrollTop el) (.-clientHeight el)) buffer-px))))

(defn element-visibility-info [el]
  (let [rect (.getBoundingClientRect el)
        viewport-w (.-innerWidth js/window)
        viewport-h (.-innerHeight js/window)
        top (.-top rect)
        bottom (.-bottom rect)
        left (.-left rect)
        right (.-right rect)
        is-visible (and (< top viewport-h)
                        (> bottom 0)
                        (< left viewport-w)
                        (> right 0))]
    {:visible? is-visible
     :offsets {:top (if (< top 0) top 0)
               :bottom (if (> bottom viewport-h) (- bottom viewport-h) 0)
               :left (if (< left 0) left 0)
               :right (if (> right viewport-w) (- right viewport-w) 0)}}))

(defn dark-mode-system-setting? []
  (let [dark-mode-mql (and (exists? js/window.matchMedia)
                           (.matchMedia js/window "(prefers-color-scheme: dark)"))]
    (if (and dark-mode-mql (.-matches dark-mode-mql))
      true
      false)))

(defn mean [nums]
  (when (not-empty nums)
    (/ (reduce + nums) (count nums))))

(defn add-query-params [params]
  (try
    (let [url (js/URL. js/window.location.href)
          search-params (.-searchParams url)]
      (doseq [[k v] params]
        (when (some? v)
          (.set search-params (name k) (str v))))
      (set! (.-search url) (.toString search-params))
      (.replaceState js/window.history nil "" (.toString url))
      (.toString url))
    (catch :default e
      )))

(defn valid-email-address? [email-address]
  (boolean (re-matches regexes/email?? (s/trim (str email-address)))))

(defn normalize-phone-number [phone-number]
  (s/replace (str phone-number) #"[\s().-]" ""))

(defn valid-phone-number? [phone-number]
  (boolean (re-matches regexes/phone-number?? (normalize-phone-number phone-number))))

(defn requirements->missing-requirements [requirements]
  (some->> (remove :valid? requirements)
           (group-by :kind)
           (mapv (fn [[k vals]]
                   (let [messages (mapv :error-message vals)]
                     [k {:message (s/join ". " messages)
                         :messages messages}])))
           (into {})))
